// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { useEffect, useMemo, useRef } from 'react';
import { Video, ResizeMode } from 'expo-av';

import { Text, View } from '@/components/Themed';
import GameWeightControl from '@/components/GameWeightControl';
import { useI18n } from '@/i18n';
import { buildMediaUrl } from '@/services/mediaUrl';

type RoundGameInfo = {
  name?: string | null;
  info?: string | null;
  description?: string | null;
  competitionType?: string | null;
  defaultWeightPercent?: number | null;
  video?: string | null;
  videoWebWidth?: number | null;
  videoWebHeight?: number | null;
  videoMobileWidth?: number | null;
  videoMobileHeight?: number | null;
};

type RoundGameInfoModalProps = {
  visible: boolean;
  game: RoundGameInfo | null;
  apiBaseUrl: string;
  onClose: () => void;
  onWeightPress?: () => void;
};

const MOBILE_VIDEO_BREAKPOINT = 900;

export default function RoundGameInfoModal({ visible, game, apiBaseUrl, onClose, onWeightPress }: RoundGameInfoModalProps) {
  const { t } = useI18n();
  const { width: viewportWidth } = useWindowDimensions();
  const infoVideoRef = useRef<Video>(null);
  const gameName = (game?.name ?? '').trim() || t('selectGames.gameNameFallback');
  const descriptionText = buildGameDescription(game, t('selectGames.infoDescriptionEmpty'), t('selectGames.infoDescriptionExtra'));
  const gameTypeText = (game?.competitionType ?? '').trim() || t('selectGames.infoGameTypeDefault');
  const weightPercent = safeWeight(game?.defaultWeightPercent, 100);
  const showWeightControl = typeof onWeightPress === 'function';

  useEffect(() => {
    if (!visible) {
      infoVideoRef.current?.stopAsync().catch(() => {});
    }
  }, [visible]);

  const infoVideoUri = useMemo(
    () => resolveGameVideoUri(game?.video, apiBaseUrl, viewportWidth),
    [apiBaseUrl, game?.video, viewportWidth]
  );

  const infoVideoLayout = useMemo(
    () => resolveInfoVideoLayout(game, viewportWidth > 0 && viewportWidth <= MOBILE_VIDEO_BREAKPOINT),
    [game, viewportWidth]
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.infoOverlay}>
        <View style={styles.infoCard}>
          <ScrollView
            style={styles.infoScroll}
            contentContainerStyle={styles.infoScrollContent}
            showsVerticalScrollIndicator
          >
            <Text style={styles.infoTitle}>
              {t('selectGames.infoVideoTitleByGame', { name: gameName })}
            </Text>
            {infoVideoUri ? (
              <Video
                ref={infoVideoRef}
                source={{ uri: infoVideoUri }}
                style={[
                  styles.infoVideo,
                  {
                    aspectRatio: infoVideoLayout.aspectRatio,
                    maxWidth: infoVideoLayout.maxWidth * 0.82,
                  },
                ]}
                resizeMode={ResizeMode.CONTAIN}
                useNativeControls
                shouldPlay={visible}
              />
            ) : null}
            <Text style={styles.infoDescriptionTitle}>{t('selectGames.infoDescriptionTitle')}</Text>
            <Text style={styles.infoDescriptionText}>{descriptionText}</Text>
            <Text style={styles.infoMetaTitle}>{t('selectGames.infoGameTypeTitle')}</Text>
            <Text style={styles.infoMetaText}>{gameTypeText}</Text>
            {showWeightControl ? (
              <>
                <Text style={styles.infoMetaTitle}>{t('selectGames.infoWeightTitle')}</Text>
                <GameWeightControl
                  weightPercent={weightPercent}
                  onPress={onWeightPress}
                  selected
                  centered={false}
                  showLabel
                />
                <Text style={styles.infoMetaText}>{t('selectGames.infoWeightValue', { weight: weightPercent })}</Text>
                <Text style={styles.infoMetaHelp}>{t('selectGames.infoWeightHelp')}</Text>
              </>
            ) : null}
            <Pressable style={styles.infoCloseButton} onPress={onClose}>
              <Text style={styles.infoCloseButtonText}>{t('selectGames.closeVideo')}</Text>
            </Pressable>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function resolveGameVideoUri(videoPath: string | null | undefined, apiBaseUrl: string, viewportWidth: number): string | null {
  const raw = (videoPath ?? '').trim();
  if (!raw) {
    return null;
  }

  const preferMobile = viewportWidth > 0 && viewportWidth <= MOBILE_VIDEO_BREAKPOINT;
  const resolvePresetAndKey = (key: string) => {
    const normalizedKey = key.replace(/^\/+/, '');
    if (normalizedKey.startsWith('video/mobile/')) {
      return { key: `video/${normalizedKey.slice('video/mobile/'.length)}`, preset: 'mobile' };
    }

    if (normalizedKey.startsWith('video/web/')) {
      return { key: `video/${normalizedKey.slice('video/web/'.length)}`, preset: 'web' };
    }

    if (!normalizedKey.startsWith('video/')) {
      return { key: normalizedKey, preset: preferMobile ? 'mobile' : 'web' };
    }

    return {
      key: normalizedKey,
      preset: preferMobile ? 'mobile' : 'web',
    };
  };

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  if (raw.startsWith('s3://')) {
    const withoutScheme = raw.slice('s3://'.length);
    const slashIndex = withoutScheme.indexOf('/');
    if (slashIndex >= 0 && slashIndex + 1 < withoutScheme.length) {
      const key = withoutScheme.slice(slashIndex + 1);
      if (key.startsWith('assets-generated/video/')) {
        const normalized = key.slice('assets-generated/'.length);
        const resolved = resolvePresetAndKey(normalized);
        return buildMediaUrl('video', resolved.key, resolved.preset);
      }

      if (key.startsWith('video/')) {
        const resolved = resolvePresetAndKey(key);
        return buildMediaUrl('video', resolved.key, resolved.preset);
      }
    }
    return null;
  }

  if (raw.startsWith('/video/')) {
    const resolved = resolvePresetAndKey(raw);
    return buildMediaUrl('video', resolved.key, resolved.preset);
  }

  if (raw.startsWith('video/')) {
    const resolved = resolvePresetAndKey(raw);
    return buildMediaUrl('video', resolved.key, resolved.preset);
  }

  if (raw.startsWith('/')) {
    return `${apiBaseUrl}${raw}`;
  }

  return `${apiBaseUrl}/${raw}`;
}

function resolveInfoVideoLayout(game: RoundGameInfo | null, preferMobile: boolean) {
  const webWidth = safePositive(game?.videoWebWidth, 1280);
  const webHeight = safePositive(game?.videoWebHeight, 720);
  const mobileWidth = safePositive(game?.videoMobileWidth, 300);
  const mobileHeight = safePositive(game?.videoMobileHeight, 530);
  const width = preferMobile ? mobileWidth : webWidth;
  const height = preferMobile ? mobileHeight : webHeight;

  return {
    aspectRatio: width / height,
    maxWidth: width,
  };
}

function safePositive(value: number | null | undefined, fallback: number) {
  return typeof value === 'number' && value > 0 ? value : fallback;
}

function safeWeight(value: number | null | undefined, fallback: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback;
  }

  return Math.max(0, Math.min(100, Math.round(value)));
}

function buildGameDescription(game: RoundGameInfo | null, emptyFallback: string, extraText: string) {
  const primary = (game?.description ?? '').trim();
  const secondary = (game?.info ?? '').trim();
  const merged = primary && secondary && primary !== secondary ? `${primary} ${secondary}` : (primary || secondary || emptyFallback);
  return `${merged} ${extraText}`.trim();
}

const styles = StyleSheet.create({
  infoOverlay: {
    flex: 1,
    backgroundColor: 'rgba(4,7,17,0.72)',
    justifyContent: 'center',
    padding: 20,
  },
  infoCard: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '86%',
    alignSelf: 'center',
    backgroundColor: '#07200D',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 12,
  },
  infoScroll: {
    width: '100%',
  },
  infoScrollContent: {
    gap: 12,
    paddingBottom: 2,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#F5F5DC',
    textAlign: 'center',
  },
  infoVideo: {
    width: '82%',
    height: undefined,
    borderRadius: 12,
    backgroundColor: '#e9eeea',
    alignSelf: 'center',
  },
  infoDescriptionTitle: {
    marginTop: 4,
    color: '#2d6b66',
    fontSize: 12,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  infoDescriptionText: {
    color: '#F5F5DC',
    marginTop: -4,
    lineHeight: 19,
  },
  infoMetaTitle: {
    color: '#2d6b66',
    fontSize: 12,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontWeight: '700',
    marginTop: 4,
  },
  infoMetaText: {
    color: '#F5F5DC',
    marginTop: -4,
    lineHeight: 18,
  },
  infoMetaHelp: {
    color: '#C8C8B0',
    marginTop: -4,
    lineHeight: 17,
    fontSize: 13,
  },
  infoCloseButton: {
    alignSelf: 'center',
    backgroundColor: '#A1D493',
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 10,
  },
  infoCloseButtonText: {
    color: '#F5F5DC',
    fontWeight: '700',
  },
});
